import { useEffect, useState } from 'react';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type LeadType, type ObjectType, type TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import FormInput from 'components/forms/FormInput';
import TaskStatusBadge from '../../shared/TaskStatusBadge';
import TaskTypeChip from '../../shared/TaskTypeChip';
import TaskActions from '../TaskActions';
import { canEditTaskTerm, canExecutorChangeTerm } from '../../shared/helpers/taskDueDateHelpers';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import SolarIcon from 'components/icons/SolarIcon';
import TaskCardBindingSection from './general/TaskCardBindingSection';
import TaskCardCategorySection from './general/TaskCardCategorySection';
import TaskCardDescriptionSection from './general/TaskCardDescriptionSection';
import TaskCardKanbanHideSection from './general/TaskCardKanbanHideSection';
import TaskCardTermSection from './general/TaskCardTermSection';

interface TaskCardGeneralTabProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onRequestResponse: () => void;
    showRejectForm: boolean;
    onShowRejectFormChange: (show: boolean) => void;
}

const formatCreatedAt = (millis: number): string => {
    if (!millis) return '—';
    const dt = DT.fromMillis(millis, { zone: 'local' });
    return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '—';
};

const hasHtmlContent = (html: string): boolean => {
    const text = String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    return text.length > 0;
};

const toLeadValue = (task: TaskCardType): LeadType | null => {
    if (!task.leadInfo?.ID) return null;
    return {
        ID: task.leadInfo.ID,
        clientName: task.leadInfo.clientName || '',
        clientPhone: task.leadInfo.clientPhone || '',
        leadStatus: Number(task.leadInfo.leadStatus || 0),
    } as LeadType;
};

const toObjectValue = (task: TaskCardType): ObjectType | null => {
    if (!task.objectInfo?.ID) return null;
    return {
        ID: task.objectInfo.ID,
        objectName: task.objectInfo.objectName || '',
        clientName: task.objectInfo.clientName || '',
        onlySale: task.objectInfo.onlySale,
        onlyService: task.objectInfo.onlyService,
    } as ObjectType;
};

const TaskCardGeneralTab = ({
    task,
    onTaskUpdated,
    onRequestResponse,
    showRejectForm,
    onShowRejectFormChange,
}: TaskCardGeneralTabProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const [editingTerm, setEditingTerm] = useState(false);
    const [editingExecutorTerm, setEditingExecutorTerm] = useState(false);
    const [editingBinding, setEditingBinding] = useState(false);
    const [kanbanHideSaving, setKanbanHideSaving] = useState(false);
    const showDescription = hasHtmlContent(task.taskDescription || '');
    const leadValue = toLeadValue(task);
    const objectValue = toObjectValue(task);

    const userId = Number(auth?.user?.ID || 0);
    const status = Number(task.taskStatus);
    const isOwner = userId > 0 && userId === Number(task.userId);
    const isController = userId > 0 && userId === Number(task.taskController);
    const isDeleted = Boolean(Number(task.taskDeleted));
    const canEditTerm = canEditTaskTerm(task, userId);
    const canChangeExecutorTerm = canExecutorChangeTerm(task, userId);
    const canEditBinding = isOwner && !isDeleted;
    const canEditCategory = isOwner && !isDeleted;
    const canToggleKanbanHide =
        !isDeleted &&
        (isOwner || isController) &&
        status === TaskTypeStatus.ACCEPTED;
    const showKanbanHide = status === TaskTypeStatus.ACCEPTED;

    useEffect(() => {
        if (Number(task.taskStatus) !== TaskTypeStatus.COMPLETED && showRejectForm) {
            onShowRejectFormChange(false);
        }
    }, [task.taskStatus, showRejectForm, onShowRejectFormChange]);

    useEffect(() => {
        if (!canEditTerm) setEditingTerm(false);
    }, [canEditTerm]);

    useEffect(() => {
        if (!canEditBinding) setEditingBinding(false);
    }, [canEditBinding]);

    const handleReject = async (value: string | number) => {
        const comment = String(value || '').trim();
        if (!comment) {
            showToast(t('errEmptyReject'), 'danger');
            return false;
        }
        const result = await apiTask.rejectTaskAnswer(task.ID, { comment });
        if (!result?.stat || !result.task) {
            showToast(
                result?.errCode === 'EMPTY_COMMENT' ? t('errEmptyReject') : t('common:messError'),
                'danger',
            );
            return false;
        }
        showToast(t('messReturnedToWork'), 'success');
        onTaskUpdated(result.task);
        return true;
    };

    const handleKanbanHideChange = async (checked: boolean) => {
        if (!canToggleKanbanHide || kanbanHideSaving) return;
        setKanbanHideSaving(true);
        const result = await apiTask.updateTaskKanbanHide(task.ID, { hide: checked });
        setKanbanHideSaving(false);
        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('messKanbanHideUpdated'), 'success');
        onTaskUpdated(result.task);
    };

    return (
        <>
            <div className="task-card-hero mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <TaskTypeChip
                        name={tDirName(t, task.taskTypeI18nKey, task.taskTypeName)}
                        icon={task.taskTypeIcon}
                        colors={task.taslTypeJson}
                    />
                    <TaskStatusBadge
                        status={Number(task.taskStatus)}
                        dateEnd={task.dateEnd}
                        taskDeleted={task.taskDeleted}
                    />
                    <TaskActions
                        task={task}
                        onTaskUpdated={onTaskUpdated}
                        onRequestResponse={onRequestResponse}
                        onRequestReject={() => onShowRejectFormChange(!showRejectForm)}
                        rejectFormOpen={showRejectForm}
                    />
                </div>

                {showRejectForm && (
                    <div className="mb-3">
                        <FormInput
                            title={t('labelRejectComment')}
                            showForm={showRejectForm}
                            setShowForm={onShowRejectFormChange}
                            defaultValue={task.taskAnswerReject || ''}
                            as="textarea"
                            rows={5}
                            required
                            requiredMessage={t('errEmptyReject')}
                            callBack={handleReject}
                        />
                    </div>
                )}

                <h4 className="task-card-title mb-1">{task.taskName || '—'}</h4>
                <div className="task-card-meta">
                    <SolarIcon name="clock-history" className="me-1" />
                    {t('fields.created')}: {formatCreatedAt(Number(task.dt || 0))}
                </div>
            </div>

            <TaskCardTermSection
                task={task}
                editingTerm={editingTerm}
                editingExecutorTerm={editingExecutorTerm}
                canEditTerm={canEditTerm}
                canChangeExecutorTerm={canChangeExecutorTerm}
                onStartEditTerm={() => {
                    setEditingExecutorTerm(false);
                    setEditingTerm(true);
                }}
                onStartEditExecutorTerm={() => {
                    setEditingTerm(false);
                    setEditingExecutorTerm(true);
                }}
                onCancelTerm={() => setEditingTerm(false)}
                onCancelExecutorTerm={() => setEditingExecutorTerm(false)}
                onSaved={(next) => {
                    setEditingTerm(false);
                    setEditingExecutorTerm(false);
                    onTaskUpdated(next);
                }}
            />

            <TaskCardCategorySection
                task={task}
                canEdit={canEditCategory}
                onSaved={onTaskUpdated}
            />

            <TaskCardBindingSection 
                task={task}
                editing={editingBinding}
                objectValue={objectValue}
                leadValue={leadValue}
                canEdit={canEditBinding}
                onStartEdit={() => setEditingBinding(true)}
                onCancel={() => setEditingBinding(false)}
                onSaved={(next) => {
                    setEditingBinding(false);
                    onTaskUpdated(next);
                }}
            />

            {showDescription && <TaskCardDescriptionSection html={task.taskDescription || ''} />}

            {showKanbanHide && (
                <TaskCardKanbanHideSection
                    id={`task-${task.ID}-kanban-hide`}
                    checked={Boolean(task.taskKanbanHide)}
                    disabled={!canToggleKanbanHide || kanbanHideSaving}
                    onChange={(checked) => void handleKanbanHideChange(checked)}
                />
            )}
        </>
    );
};

export default TaskCardGeneralTab;
